import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Toast from 'react-hot-toast';
import { CatalogForm } from '../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../components/CatalogForm/validators';
import { ClienteService } from '../../../services/ClienteService';

export const ClienteForm = ({ id: propId, onSave, onCancel, backUrl = '/Catalogo/Clientes', isModal = false }) => {
  const routeParams = useParams() || {};
  const history = useHistory();
  const id = propId !== undefined ? propId : (routeParams.id || null);
  const isEdit = Boolean(id);

  const [estadosOptions, setEstadosOptions] = useState([]);
  const [ciudadesOptions, setCiudadesOptions] = useState([]);
  const [coloniasOptions, setColoniasOptions] = useState([]);

  // Cargar catálogos iniciales (estados)
  useEffect(() => {
    let isMounted = true;
    const fetchCatalogos = async () => {
      try {
        const data = await ClienteService.obtenerFormGenerales();
        if (isMounted && data) {
          if (Array.isArray(data.estados)) {
            setEstadosOptions(data.estados.map((e) => ({ value: e.id, label: e.estado })));
          }
          if (Array.isArray(data.ciudades)) {
            setCiudadesOptions(data.ciudades.map((c) => ({ value: c.id, label: c.ciudad })));
          }
        }
      } catch (err) {
        console.error('Error al cargar opciones de clientes:', err);
      }
    };

    fetchCatalogos();
    return () => {
      isMounted = false;
    };
  }, []);

  // Pre-cargar datos si se está editando
  const loadData = useCallback(async (recordId) => {
    try {
      const data = await ClienteService.obtenerPorId(recordId);
      if (data) {
        // Asegurar tipos correctos para los campos
        if (data.cliente_fm !== undefined && data.cliente_fm !== null) {
          data.cliente_fm = String(data.cliente_fm);
        }
        data.cliente_dv = Boolean(data.cliente_dv);

        // Si tiene CP, precargar sus colonias
        if (data.cliente_cp && String(data.cliente_cp).length === 5) {
          const formGen = await ClienteService.obtenerFormGenerales({ cp: data.cliente_cp });
          if (formGen && formGen.direccion) {
            const { colonias, ciudades } = formGen.direccion;
            if (Array.isArray(colonias)) {
              setColoniasOptions(colonias.map((c) => ({ value: c.id, label: c.colonia })));
            }
            if (Array.isArray(ciudades)) {
              setCiudadesOptions(ciudades.map((c) => ({ value: c.id, label: c.ciudad })));
            }
          }
        }
        return data;
      }
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }, []);

  // Manejo dinámico de Código Postal
  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    if (!val || String(val).trim().length !== 5) {
      setColoniasOptions([]);
      return;
    }
    const cleanCp = String(val).trim().substring(0, 5);
    try {
      const formGen = await ClienteService.obtenerFormGenerales({ cp: cleanCp });
      if (formGen && formGen.direccion) {
        const { direccion } = formGen;
        if (direccion.estado && direccion.estado.id) {
          setFieldValue('cliente_id_estado', direccion.estado.id);
        }
        if (direccion.ciudad && direccion.ciudad.id) {
          setFieldValue('cliente_id_ciudad', direccion.ciudad.id);
        }
        if (Array.isArray(direccion.ciudades)) {
          setCiudadesOptions(direccion.ciudades.map((c) => ({ value: c.id, label: c.ciudad })));
        }
        if (Array.isArray(direccion.colonias)) {
          const mappedCols = direccion.colonias.map((c) => ({ value: c.id, label: c.colonia }));
          setColoniasOptions(mappedCols);
          if (mappedCols.length === 1) {
            setFieldValue('cliente_id_colonia', mappedCols[0].value);
          } else {
            setFieldValue('cliente_id_colonia', '');
          }
        }
      }
    } catch (e) {
      console.error('Error al consultar CP:', e);
    }
  }, []);

  // Manejo de cambio de Estado para cargar Ciudades
  const handleEstadoChange = useCallback(async (val, { setFieldValue }) => {
    if (!val) return;
    try {
      const formGen = await ClienteService.obtenerFormGenerales({ e: val });
      if (formGen && Array.isArray(formGen.ciudades)) {
        setCiudadesOptions(formGen.ciudades.map((c) => ({ value: c.id, label: c.ciudad })));
        setFieldValue('cliente_id_ciudad', '');
      }
    } catch (e) {
      console.error('Error al consultar ciudades por estado:', e);
    }
  }, []);

  // Validación de RFC según persona física o moral
  const validateRfc = useCallback((val, allValues) => {
    if (!val) return 'El RFC es obligatorio';
    const clean = String(val).trim().toUpperCase();
    const isFisica = String(allValues.cliente_fm) === '1';

    if (isFisica) {
      const rfcFisicaRegex = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/;
      if (!rfcFisicaRegex.test(clean)) {
        return 'RFC de persona física no válido (debe tener 13 caracteres: 4 letras, 6 números y 3 homoclave)';
      }
    } else {
      const rfcMoralRegex = /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/;
      if (!rfcMoralRegex.test(clean)) {
        return 'RFC de persona moral no válido (debe tener 12 caracteres: 3 letras, 6 números y 3 homoclave)';
      }
    }
    return null;
  }, []);

  // Definición de campos del formulario
  const fields = useMemo(() => {
    const list = [
      {
        name: 'sec_generales',
        label: 'Datos Principales del Cliente',
        type: 'section',
        helperText: 'Información general de identificación y fiscal del cliente',
      },
      {
        name: 'cliente',
        label: 'Clave de Cliente',
        type: 'text',
        col: 4,
        required: true,
        placeholder: 'Ej. 1001',
        validators: [Validators.required('La clave del cliente es obligatoria')],
      },
      {
        name: 'cliente_rsocial',
        label: 'Razón Social',
        type: 'text',
        col: 8,
        required: true,
        placeholder: 'Ej. TRANSPORTES NACIONALES S.A. DE C.V.',
        validators: [Validators.required('La razón social es obligatoria')],
      },
      {
        name: 'cliente_fm',
        label: 'Tipo de Persona',
        type: 'select',
        col: 4,
        required: true,
        options: [
          { value: '1', label: 'Persona Física' },
          { value: '2', label: 'Persona Moral' },
        ],
        validators: [Validators.required('El tipo de persona es obligatorio')],
      },
      {
        name: 'cliente_rfc',
        label: 'R.F.C.',
        type: 'text',
        col: 5,
        required: true,
        placeholder: 'Ej. XAXX010101000',
        validators: [Validators.required('El RFC es obligatorio'), validateRfc],
      },
      {
        name: 'cliente_dv',
        label: 'Repetir RFC',
        type: 'checkbox',
        col: 3,
        helperText: 'Marcar para permitir RFC repetido',
      },
      {
        name: 'cliente_desc',
        label: 'Descripción / Observaciones',
        type: 'text',
        col: 12,
        placeholder: 'Notas o descripción del cliente',
      },
    ];

    // Subsección condicional de Domicilio Inicial (Solo requerida al dar de alta un nuevo cliente)
    if (!isEdit) {
      list.push(
        {
          name: 'sec_domicilio',
          label: 'Domicilio Inicial',
          type: 'section',
          helperText: 'Dirección principal para remitente y destinatario base del nuevo cliente',
        },
        {
          name: 'cliente_cp',
          label: 'Código Postal',
          type: 'text',
          col: 3,
          required: true,
          placeholder: '5 dígitos (ej. 44100)',
          onChange: handleCpChange,
          validators: [Validators.required('El código postal es obligatorio')],
        },
        {
          name: 'cliente_id_colonia',
          label: 'Colonia',
          type: 'select',
          col: 5,
          required: true,
          options: coloniasOptions,
          validators: [Validators.required('La colonia es obligatoria')],
        },
        {
          name: 'cliente_domicilio',
          label: 'Calle y Número / Domicilio',
          type: 'text',
          col: 8,
          required: true,
          placeholder: 'Ej. Av. Juárez #500',
          validators: [Validators.required('El domicilio es obligatorio')],
        },
        {
          name: 'cliente_next',
          label: 'N. Ext',
          type: 'text',
          col: 2,
          placeholder: 'Ext.',
        },
        {
          name: 'cliente_nint',
          label: 'N. Int',
          type: 'text',
          col: 2,
          placeholder: 'Int.',
        },
        {
          name: 'cliente_id_estado',
          label: 'Estado',
          type: 'select',
          col: 6,
          required: true,
          options: estadosOptions,
          onChange: handleEstadoChange,
          validators: [Validators.required('El estado es obligatorio')],
        },
        {
          name: 'cliente_id_ciudad',
          label: 'Ciudad / Municipio',
          type: 'select',
          col: 6,
          required: true,
          options: ciudadesOptions,
          validators: [Validators.required('La ciudad o municipio es obligatoria')],
        }
      );
    }

    return list;
  }, [isEdit, coloniasOptions, estadosOptions, ciudadesOptions, handleCpChange, handleEstadoChange, validateRfc]);

  // Envío del formulario
  const handleSubmit = useCallback(
    async (values) => {
      const payload = {
        ...values,
        id: id || undefined,
        cliente_rfc: values.cliente_rfc ? String(values.cliente_rfc).trim().toUpperCase() : '',
        cliente_fm: values.cliente_fm ? Number(values.cliente_fm) : 1,
        cliente_dv: values.cliente_dv ? 1 : 0,
      };

      const res = await ClienteService.guardar(payload);
      return res;
    },
    [id]
  );

  const handleSuccess = useCallback(
    (response) => {
      Toast.success(isEdit ? 'Cliente actualizado correctamente' : 'Cliente creado exitosamente');
      if (onSave) {
        onSave(response);
      } else if (!isEdit) {
        const newId = response?.data?.id || response?.data?.data?.id || response?.id;
        if (newId) {
          history.push(`/Catalogo/Cliente/${newId}/Generales`);
        } else {
          history.push('/Catalogo/Clientes');
        }
      }
    },
    [isEdit, onSave, history]
  );

  return (
    <CatalogForm
      title={isEdit ? 'Editar Cliente' : 'Nuevo Cliente'}
      subtitle={
        isEdit
          ? 'Actualice los datos principales de identificación del cliente'
          : 'Complete la información general y domicilio del nuevo cliente'
      }
      fields={fields}
      initialValues={{
        cliente: '',
        cliente_rsocial: '',
        cliente_fm: '1',
        cliente_rfc: '',
        cliente_dv: false,
        cliente_desc: '',
        cliente_cp: '',
        cliente_id_colonia: '',
        cliente_domicilio: '',
        cliente_next: '',
        cliente_nint: '',
        cliente_id_estado: '',
        cliente_id_ciudad: '',
      }}
      id={id}
      loadData={loadData}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
      backUrl={backUrl}
    />
  );
};

export default ClienteForm;
